import React, { useState, useEffect } from "react";
import "../AddAdvertisement.css";
import KeywordSelectionModal from "../components/KeywordSelectionModal";
import WeekdayOnlyDatePicker from "../components/WeekdayOnlyDatePicker";
import { isSpecialUser } from "../../../../../utils/environment";

const CoupangVisitPage = ({
  formData,
  onInputChange,
  user,
  onNext,
  endSubmitTime,
  step = 1,
  keywordRanks = [],
  addKeywordRank,
  removeKeywordRank,
  updateKeywordRank,
  onKeywordRanksChange,
  zoomSettings,
}) => {
  const [urlError, setUrlError] = useState("");
  const [isKeywordModalOpen, setIsKeywordModalOpen] = useState(false);
  const [keywordLoadingIndex, setKeywordLoadingIndex] = useState(null); // 키워드 순위 조회 중인 인덱스

  // adStartDate와 adEndDate가 변경될 때 광고 기간 자동 계산
  // 주의: 이 useEffect는 사용자가 직접 날짜를 입력할 때만 실행되어야 하므로,
  // 탭 변경으로 인한 초기화 시에는 실행되지 않도록 주의해야 함
  useEffect(() => {
    // 빈 문자열이거나 undefined인 경우 실행하지 않음 (초기화 방지)
    if (!formData.adStartDate || !formData.adEndDate || 
        formData.adStartDate === "" || formData.adEndDate === "") {
      return;
    }

    const startDate = new Date(formData.adStartDate);
    const endDate = new Date(formData.adEndDate);

    // 유효한 날짜인지 확인
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return;
    }

    if (endDate >= startDate) {
      const timeDiff = endDate.getTime() - startDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

      // 진행 일수는 30의 배수일 때만 자동 반영
      if (daysDiff >= 30 && daysDiff % 30 === 0) {
        onInputChange("adDuration", daysDiff.toString());
      }
    }
  }, [formData.adStartDate, formData.adEndDate]);

  // 내일 날짜 계산 (한국 시간 기준)
  const getTomorrowDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    const tomorrow = new Date(koreaTime);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // 오늘 날짜 계산 (한국 시간 기준)
  const getTodayDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    return koreaTime.toISOString().split("T")[0];
  };

  // 최소 날짜 (내일)
  const minDate = getTomorrowDate();
  const todayDate = getTodayDate();

  // 특정 유저는 오늘부터, 일반 유저는 내일부터
  const userIsSpecial = isSpecialUser(user?.id);
  const effectiveMinDate = userIsSpecial ? todayDate : minDate;

  // 광고 종료일의 최소 날짜 (광고 시작일이 있으면 그 날짜, 없으면 내일)
  const getEndDateMin = () => {
    if (formData.adStartDate) {
      return formData.adStartDate;
    }
    return effectiveMinDate;
  };

  // 날짜 검증 함수
  const validateDate = (dateString) => {
    if (!dateString) return "날짜를 선택해주세요.";

    const selectedDate = new Date(dateString);
    const tomorrow = new Date(getTomorrowDate());

    // 유효한 날짜인지 확인
    if (isNaN(selectedDate.getTime())) {
      return "올바른 날짜 형식이 아닙니다.";
    }

    // 특정 유저가 아닌 경우에만 내일 이후인지 확인
    if (!userIsSpecial && selectedDate < tomorrow) {
      return "광고 시작일은 내일 이후여야 합니다.";
    }

    return "";
  };

  // 광고 시작일 입력 처리
  const handleAdStartDateChange = (e) => {
    const value = e.target.value;

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adStartDate", "");
        alert(error);
        return;
      }
      // 광고 시작일: 주말(토·일) 선택 불가 (VVIP 또는 특정 유저는 주말 가능)
      const selectedDate = new Date(value);
      const dayOfWeek = selectedDate.getDay();
      const allowWeekendByGrade = user?.price_grade?.name === "VVIP";
      const allowWeekend = allowWeekendByGrade || userIsSpecial;

      if ((dayOfWeek === 0 || dayOfWeek === 6) && !allowWeekend) {
        onInputChange("adStartDate", "");
        alert("광고 시작일은 평일만 선택 가능합니다.");
        return;
      }
    }

    // 광고 시작일 업데이트
    onInputChange("adStartDate", value);

    // 광고 시작일이 변경되면 모든 계산 재실행
    if (value) {
      // 진행 일수가 있으면 종료일을 다시 계산 (우선순위)
      const duration = parseInt(formData.adDuration) || 0;
      if (duration > 0) {
        const startDate = new Date(value);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + duration - 1); // 시작일 포함이므로 -1
        const endDateString = endDate.toISOString().split("T")[0];
        onInputChange("adEndDate", endDateString);
      } else if (formData.adEndDate) {
        // 종료일이 있으면 진행 일수 검증만 수행 (자동 채우지 않음)
        // useEffect에서 자동으로 계산되므로 여기서는 별도 처리 불필요
      }
    } else {
      // 광고 시작일이 비어있으면 종료일과 진행 일수 초기화
      onInputChange("adEndDate", "");
      onInputChange("adDuration", "");
    }
  };

  // 광고 종료일 입력 처리
  const handleAdEndDateChange = (e) => {
    const value = e.target.value;
    onInputChange("adEndDate", value);

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adEndDate", "");
        alert(error);
        return;
      }

      // 광고 시작일이 있으면 진행 일수 검증만 수행 (자동 채우지 않음)
      if (formData.adStartDate) {
        const startDate = new Date(formData.adStartDate);
        const endDate = new Date(value);

        if (endDate >= startDate) {
          const timeDiff = endDate.getTime() - startDate.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

          // 진행 일수는 최소 30일, 30의 배수여야 함
          if (daysDiff < 30) {
            // 에러는 표시하지 않고, 진행 일수는 자동으로 채우지 않음
            // 사용자가 직접 진행 일수를 입력하도록 함
          } else if (daysDiff % 30 !== 0) {
            // 30의 배수가 아니면 에러 표시하지 않고, 진행 일수는 자동으로 채우지 않음
            // 사용자가 직접 진행 일수를 입력하도록 함
          }
          // 30의 배수이면 useEffect에서 자동으로 adDuration이 계산됨
        }
      }
    }
    // calculateAdDuration() 호출 제거 - 진행 일수는 자동으로 채우지 않음
  };

  // 진행 일수 입력 처리 (입력 중에는 검증 없이 허용, 30의 배수일 때만 종료일 계산)
  const handleAdDurationChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 허용

    if (!formData.adStartDate) {
      alert("광고 시작일을 먼저 선택해주세요.");
      return;
    }

    if (value === "") {
      onInputChange("adDuration", "");
      onInputChange("adEndDate", "");
      return;
    }

    onInputChange("adDuration", value);

    const duration = parseInt(value) || 0;
    // 30일 이상이고 30의 배수일 때만 종료일 계산 (입력 중 "3", "6" 등은 종료일 미반영)
    if (duration >= 30 && duration % 30 === 0) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + duration - 1); // 시작일 포함이므로 -1
      onInputChange("adEndDate", endDate.toISOString().split("T")[0]);
    } else {
      onInputChange("adEndDate", "");
    }
  };

  // 진행 일수 포커스 아웃 시 검증 (30일 이상, 30의 배수)
  const handleAdDurationBlur = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value === "") return;

    const duration = parseInt(value) || 0;
    if (duration < 30 || duration % 30 !== 0) {
      alert("진행 일수는 30일 이상이며 30의 배수로 입력해주세요. (예: 30, 60, 90일)");
      onInputChange("adDuration", "");
      onInputChange("adEndDate", "");
    }
  };

  // 광고 기간 자동 계산 함수
  const calculateAdDuration = () => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  };

  // 결제 금액 자동 계산 함수 (1회 단가 소수점 지원)
  const calculatePrice = () => {
    const dailyQuantity = parseInt(formData.dailyQuantity) || 0;
    const adDuration = parseInt(formData.adDuration) || 0;
    const userPriceByOne = parseFloat(formData.userPriceByOne) || 0;

    const totalQuantity = dailyQuantity * adDuration;
    const totalPrice = totalQuantity * userPriceByOne;

    onInputChange("totalQuantity", totalQuantity.toString());
    onInputChange("price", totalPrice.toString());
  };

  // 일일 수량, 광고 기간, 단가가 변경될 때마다 결제 금액 계산
  useEffect(() => {
    calculatePrice();
  }, [formData.dailyQuantity, formData.adDuration, formData.userPriceByOne]);

  // URL 포맷팅 함수: 쿠팡 상품 URL은 path + itemId, vendorItemId만 유지
  const formatUrl = (url) => {
    if (!url) return "";

    try {
      const trimmed = url.trim();
      if (!trimmed) return "";

      if (
        /^https?:\/\/([^/]*\.)?coupang\.com\/vp\/products\//i.test(trimmed)
      ) {
        const [pathPart, queryPart] = trimmed.split("?");
        const pathNoTrailing = pathPart.replace(/\/$/, "");
        if (!queryPart) return pathNoTrailing;

        const params = new URLSearchParams(queryPart);
        const itemId = params.get("itemId");
        const vendorItemId = params.get("vendorItemId");
        const kept = [];
        if (itemId != null) kept.push(`itemId=${encodeURIComponent(itemId)}`);
        if (vendorItemId != null)
          kept.push(`vendorItemId=${encodeURIComponent(vendorItemId)}`);
        if (kept.length === 0) return pathNoTrailing;
        return `${pathNoTrailing}?${kept.join("&")}`;
      }

      let formattedUrl = trimmed.split("?")[0];
      formattedUrl = formattedUrl.replace(/\/$/, "");
      return formattedUrl;
    } catch {
      return url;
    }
  };

  // URL 검증 함수
  const validateUrl = (url) => {
    if (!url) return "";

    // 일반적인 URL 패턴 검증
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/;

    if (!urlPattern.test(url)) {
      return "올바른 URL 형식이 아닙니다.";
    }

    return "";
  };

  // URL 입력 처리
  const handleUrlChange = (e) => {
    const value = e.target.value;
    onInputChange("productUrl", value);
    setUrlError(""); // 입력 중에는 에러 메시지 제거
  };

  // URL 포커스 아웃 처리
  const handleUrlBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const formattedUrl = formatUrl(value);
      const error = validateUrl(formattedUrl);

      if (error) {
        setUrlError(error);
      } else {
        onInputChange("productUrl", formattedUrl);
        setUrlError("");
      }
    }
  };

  // 주문하기 버튼 클릭 시 부모 컴포넌트의 handleSubmit 호출
  const handleSubmit = () => {
    if (!user?.id) {
      onNext();
      return;
    }
    // 부모 컴포넌트의 handleSubmit을 직접 호출
    // onNext는 handleNext를 호출하므로, 여기서는 그냥 onNext를 호출하면 됨
    // 실제로는 AddAdvertisement.js에서 selectedSubTab === 13일 때 handleNext가 handleSubmit을 호출하도록 수정해야 함
    onNext();
  };

  // 키워드 모달 핸들러들
  const handleOpenKeywordModal = () => {
    setIsKeywordModalOpen(true);
  };

  const handleCloseKeywordModal = () => {
    setIsKeywordModalOpen(false);
  };

  const handleKeywordRanksChange = (newKeywordRanks) => {
    // 부모 컴포넌트의 keywordRanks를 업데이트
    if (onKeywordRanksChange) {
      onKeywordRanksChange(newKeywordRanks);
    }
  };

  // 키워드 입력 변경 핸들러
  const handleKeywordChange = (e, actualIndex) => {
    const inputValue = e.target.value;
    updateKeywordRank(actualIndex, "keyword", inputValue);
  };

  // 키워드 blur 핸들러
  const handleKeywordBlurWithValidation = (keyword, index, actualIndex) => {
    const trimmedKeyword = keyword.trim();

    if (keyword !== trimmedKeyword) {
      updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
    }

    if (trimmedKeyword !== "") {
      const duplicateActualIndex = keywordRanks.findIndex(
        (item, idx) =>
          idx !== actualIndex &&
          item.keyword.trim() !== "" &&
          item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
      );

      if (duplicateActualIndex !== -1) {
        const duplicateKeyword = keywordRanks[duplicateActualIndex];

        if (duplicateKeyword.selected) {
          alert("이미 등록된 키워드입니다.");
          removeKeywordRank(actualIndex);
          return;
        }

        updateKeywordRank(duplicateActualIndex, "selected", true);
        removeKeywordRank(actualIndex);
        return;
      }
    }
  };

  // 키워드 입력 필드 키 다운 처리 (엔터 키)
  const handleKeywordKeyDown = (e, keyword, index, actualIndex) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedKeyword = keyword.trim();

      if (keyword !== trimmedKeyword) {
        updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
      }

      if (trimmedKeyword !== "") {
        const duplicateActualIndex = keywordRanks.findIndex(
          (item, idx) =>
            idx !== actualIndex &&
            item.keyword.trim() !== "" &&
            item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
        );

        if (duplicateActualIndex !== -1) {
          const duplicateKeyword = keywordRanks[duplicateActualIndex];

          if (duplicateKeyword.selected) {
            alert("이미 등록된 키워드입니다.");
            removeKeywordRank(actualIndex);
            return;
          }

          updateKeywordRank(duplicateActualIndex, "selected", true);
          removeKeywordRank(actualIndex);
          return;
        }
      }

      e.target.blur();
    }
  };

  return (
    <div className="shopping-visit-page">
      <div className="add-form-fields">
        {/* 첫 번째 줄: 상품명 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>상품명</label>
            <input
              type="text"
              placeholder="상품명을 입력해주세요"
              value={formData.missionName || ""}
              onChange={(e) => onInputChange("missionName", e.target.value)}
              disabled={!user?.id}
            />
          </div>
        </div>

        {/* 두 번째 줄: 상품 URL 주소와 메인키워드 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>상품 URL 주소</label>
            <input
              type="text"
              placeholder="https://www.coupang.com/vp/products/..."
              value={formData.productUrl}
              onChange={handleUrlChange}
              onBlur={handleUrlBlur}
              disabled={!user?.id}
              className={urlError ? "error" : ""}
            />
            {urlError && <p className="error-message">{urlError}</p>}
          </div>
          <div className="add-form-field">
            <label>메인키워드(순위체크용)</label>
            <input
              type="text"
              placeholder="필수입력"
              value={formData.mainKeyword?.name || ""}
              onChange={(e) => onInputChange("mainKeyword", e.target.value)}
              disabled={!user?.id}
            />
          </div>
        </div>

        {/* 세 번째 줄: 일일 수량 */}
        <div className="add-form-row daily-quantity-row">
          <div className="add-form-field">
            <label>일일 수량</label>
            <input
              type="text"
              placeholder="400회 이상, 400의 배수 입력"
              value={formData.dailyQuantity}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                onInputChange("dailyQuantity", value);
              }}
              disabled={!user?.id}
            />
          </div>
        </div>

        {/* 네 번째 줄: 광고 시작일, 진행 일수, 광고 종료일 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>광고 시작일</label>
            <WeekdayOnlyDatePicker
              value={formData.adStartDate}
              onChange={(dateStr) =>
                handleAdStartDateChange({ target: { value: dateStr } })
              }
              minDate={effectiveMinDate}
              disabled={!user?.id}
              userId={user?.id}
            />
          </div>
          <div className="add-form-field">
            <label>진행 일수</label>
            <input
              type="text"
              placeholder="30의 배수 입력 (예: 30, 60, 90)"
              value={formData.adDuration || ""}
              onChange={handleAdDurationChange}
              onBlur={handleAdDurationBlur}
              disabled={!user?.id || !formData.adStartDate}
            />
          </div>
          <div className="add-form-field">
            <label>광고 종료일</label>
            <input
              type="text"
              value={formData.adEndDate || ""}
              readOnly
              placeholder="진행 일수 입력 시 자동 계산"
            />
          </div>
        </div>

        {/* 다섯 번째 줄: 결제 금액 정보 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>1회 단가</label>
            <input
              type="text"
              value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
              readOnly
            />
          </div>
          <div className="add-form-field">
            <label>신청 수량</label>
            <input
              type="text"
              value={`${formData.totalQuantity || 0}회`}
              readOnly
            />
          </div>
          <div className="add-form-field">
            <label>결제 금액</label>
            <input
              type="text"
              value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoupangVisitPage;
